/**
 * Permits fields of the customer data dictionary, in its order (#171): names,
 * groups, origin and examples from the 2026-09-23 workbook; descriptions from
 * the 2026-07-24 workbook where the newer build lost them (its columns are
 * shifted). Generated once from those workbooks (the generator isn't in this
 * repo), then hand-edited for #190: geo_source/address_source values the
 * served data carries, and real examples in place of 'NULLABLE' or a copy of
 * the description. Keep those fixes if it is ever regenerated.
 */
export const FIELD_DATA = [
    {
        group: 'Identity',
        name: 'permit_id',
        origin: 'Derived',
        type: 'STRING',
        description: "Stable, deterministic identifier for this permit. sha256(source_id + ':' + source_permit_number). Re-fetching the same permit always produces the same value, so this is the join/dedup key for downstream warehouses.",
        example: '8f14e45fceea167a5a36dedd4bea2543',
    },
    {
        group: 'Identity',
        name: 'source_id',
        origin: 'Derived',
        type: 'STRING',
        description: "Identifier of the jurisdiction's publishing system. Stable across our pipeline and traceable to a single source-of-truth feed (city/county portal).",
        example: 'permits_ca_los_angeles_city',
    },
    {
        group: 'Identity',
        name: 'source_permit_number',
        origin: 'Source',
        type: 'STRING',
        description: 'Permit number exactly as the jurisdiction publishes it. Use this to look the record up directly on the source portal.',
        example: 'BLD-2024-018342',
    },
    {
        group: "Permit classification (publisher's vocabulary)",
        name: 'permit_type',
        origin: 'Source',
        type: 'STRING',
        description: 'High-level category as published by the jurisdiction (BLDS v0.1 `permit_type`).',
        example: 'Building',
    },
    {
        group: "Permit classification (publisher's vocabulary)",
        name: 'permit_category',
        origin: 'Derived',
        type: 'STRING',
        description: "Normalized work-type BUCKET over the noisy raw permit_type: a first-match-wins keyword ruleset collapses each jurisdiction's coded abbreviations (short codes like E/ELE, P/PLU, MEC) into a stable taxonomy so you can aggregate across sources without knowing every jurisdiction's codes. Buckets (specific beat broad in match order): new_construction, addition_alteration, roof, electrical, plumbing, mechanical, solar_energy, pool_spa, deck_fence, demolition, sign, grading_sitework, other (non-empty but unmatched), unknown (no permit_type). Matched over COALESCE(permit_type, permit_subtype, description). Prefer this for cross-source work-type analysis; keep permit_type for the exact raw value.",
        example: 'new_construction',
    },
    {
        group: "Permit classification (publisher's vocabulary)",
        name: 'permit_type_display',
        origin: 'Derived',
        type: 'STRING',
        description: 'Human-readable permit type for feeds that publish permit_type as short codes: a per-source code->label decode (e.g. ELE->Electrical, PLU->Plumbing, MEC->Mechanical, BLD->Building). For sources/values with no decode mapping this is just the raw permit_type. The raw code is preserved in permit_type; permit_type_display is the readable label. Finer-grained than permit_category (the work-type bucket).',
        example: 'Building',
    },
    {
        group: "Permit classification (publisher's vocabulary)",
        name: 'permit_subtype',
        origin: 'Source',
        type: 'STRING',
        description: 'Source-provided subtype, where available.',
        example: 'Residential Single Family',
    },
    {
        group: "Permit classification (publisher's vocabulary)",
        name: 'permit_class',
        origin: 'Source',
        type: 'STRING',
        description: 'Residential / Commercial / Mixed / Industrial / etc. — as labeled by the source.',
        example: 'Residential',
    },
    {
        group: "Permit classification (publisher's vocabulary)",
        name: 'work_type',
        origin: 'Source',
        type: 'STRING',
        description: "Normalized work-type enum (10 values). Raw source value preserved upstream. See 'Lookup - Work Type' tab.",
        example: 'new',
    },
    {
        group: "Permit classification (publisher's vocabulary)",
        name: 'description',
        origin: 'Source',
        type: 'STRING',
        description: 'Free-text scope of work as published by the source (~53% populated). One of several descriptive inputs folded into `description_full`; for the fullest human-readable description prefer `description_full` (~95.7%) over this single field.',
        example: 'New single-family dwelling with attached garage',
    },
    {
        group: 'Tags',
        name: 'tags',
        origin: 'Derived',
        type: 'ARRAY<STRING>',
        description: 'Mollusk tags: the work, trades, equipment, property type and record kind found in the permit, normalized across every jurisdiction. A permit carries every tag that applies. See the Tags tab for all 95 tags and what each means.',
        example: 'electrical;new_build;residential',
    },
    {
        group: 'Dates & status',
        name: 'applied_date',
        origin: 'Source',
        type: 'DATE',
        description: 'Date the permit application was submitted.',
        example: '2024-01-15',
    },
    {
        group: 'Dates & status',
        name: 'issued_date',
        origin: 'Source',
        type: 'DATE',
        description: 'Date the permit was issued. This is the partition column in BigQuery — filter on `issued_date` for the fastest queries.',
        example: '2024-03-02',
    },
    {
        group: 'Dates & status',
        name: 'permit_date',
        origin: 'Derived',
        type: 'DATE',
        description: "USE THIS as the canonical permit date: COALESCE(issued_date, applied_date, final_date), then status_date when a permit has none of those (permit_date_source 'status' or 'status_migration'). ~97% populated across all sources vs the much sparser issued_date — many jurisdictions publish an applied or final date but never an issued date, so issued_date-only date buckets drop large swaths of real permits (in some jurisdictions issued_date covers only a third of the permits permit_date does). Prefer this for any date-range / timeline / recency analysis. See permit_date_source for which underlying date it came from. NULL only when the permit carries no date at all (~3%).",
        example: '2024-03-02',
    },
    {
        group: 'Dates & status',
        name: 'permit_date_source',
        origin: 'Derived',
        type: 'STRING',
        description: "Provenance of permit_date: which underlying column it was taken from — 'issued' > 'applied' > 'final' (that precedence), then 'status' for status_date, or 'status_migration' when that status_date is a system migration stamp shared by many records (Kern County CA, Livermore CA, Manteca CA), which recent-activity counts leave out. NULL when the permit had no date. Use to gauge the precision of permit_date (an 'issued' date is the true issuance; 'applied'/'final' are proxies; 'status' is only the last record update).",
        example: 'issued',
    },
    {
        group: 'Dates & status',
        name: 'final_date',
        origin: 'Source',
        type: 'DATE',
        description: 'Date the permit reached final / completed status.',
        example: '2024-11-20',
    },
    {
        group: 'Dates & status',
        name: 'status_date',
        origin: 'Source',
        type: 'DATE',
        description: "The source system's last-update date for the record, as published when Mollusk captured it. Any edit to the record can move it, not only a status change, and later fetches do not refresh it. It is not an issue or final date.",
        example: '2024-11-20',
    },
    {
        group: 'Dates & status',
        name: 'expiration_date',
        origin: 'Source',
        type: 'DATE',
        description: 'Date the permit expires if not closed by then.',
        example: '2025-03-02',
    },
    {
        group: 'Dates & status',
        name: 'status',
        origin: 'Source',
        type: 'STRING',
        description: 'Source-published status string, verbatim.',
        example: 'Issued',
    },
    {
        group: 'Dates & status',
        name: 'status_normalized',
        origin: 'Derived',
        type: 'STRING',
        description: "Status mapped to our 8-value enum. See 'Lookup - Permit Status' tab.",
        example: 'issued',
    },
    {
        group: 'Cost',
        name: 'estimated_cost',
        origin: 'Source',
        type: 'FLOAT64',
        description: 'Declared project cost in USD (BLDS v0.1 `estimated_cost`).',
        example: '250000.0',
    },
    {
        group: 'Cost',
        name: 'estimated_cost_imputed',
        origin: 'Derived',
        type: 'NUMERIC',
        description: "MODELED estimated cost — NOT source-reported. Populated ONLY for a permit that has NO usable source estimated_cost (NULL or <=0) AND a positive square_footage: it is the robust MEDIAN cost-per-square-foot of COMPARABLE permits (same permit_category, metro/CBSA, and year — with coarser CBSA-all-years / state / national fallback tiers when the exact cell is thin, min 20 comparables per cell) multiplied by this permit's square_footage. NULL when the permit already has a real cost, has no square footage, or has no comparable cell (never imputed without a real basis). This is a SEPARATE column: the real estimated_cost is NEVER overwritten. For a single 'best available' value use COALESCE(estimated_cost, estimated_cost_imputed) and check is_cost_imputed to know which you got.",
        example: null,
    },
    {
        group: 'Cost',
        name: 'is_cost_imputed',
        origin: 'Derived',
        type: 'BOOL',
        description: "TRUE iff estimated_cost_imputed is populated (i.e. this permit's cost is MODELED, not source-reported), FALSE otherwise. Use it to filter or flag modeled costs — e.g. exclude imputed rows from a source-cost analysis, or surface them separately.",
        example: 'false',
    },
    {
        group: 'Cost',
        name: 'cost_basis',
        origin: 'Derived',
        type: 'STRING',
        description: 'Human-readable provenance for estimated_cost_imputed: the tier used, the matched median $/sqft, the comparable sample size, and the matched key — e.g. "modeled: median $142.5/sqft of 318 addition_alteration permits; basis category_cbsa_year, CBSA 25540, 2024 (MODELED, not source-reported)". NULL when the cost was not imputed.',
        example: null,
    },
    {
        group: 'Cost',
        name: 'total_fee',
        origin: 'Source',
        type: 'FLOAT64',
        description: 'Total fee collected in USD (BLDS v0.1 `total_fee`).',
        example: '3820.5',
    },
    {
        group: 'Location (job site)',
        name: 'address_line1',
        origin: 'Source',
        type: 'STRING',
        description: 'Primary street address line.',
        example: '123 MAIN ST',
    },
    {
        group: 'Location (job site)',
        name: 'address_line2',
        origin: 'Source',
        type: 'STRING',
        description: 'Secondary address line (unit, suite, etc.).',
        example: 'APT 4',
    },
    {
        group: 'Location (job site)',
        name: 'city',
        origin: 'Source',
        type: 'STRING',
        description: 'City name as published.',
        example: 'Los Angeles',
    },
    {
        group: 'Location (job site)',
        name: 'state',
        origin: 'Source',
        type: 'STRING',
        description: 'Two-letter USPS state code, sourced from the row when available.',
        example: 'CA',
    },
    {
        group: 'Location (job site)',
        name: 'postal_code',
        origin: 'Source',
        type: 'STRING',
        description: 'ZIP code of the job site as the jurisdiction publishes it. Often blank; std_postal_code fills many gaps.',
        example: '90012',
    },
    {
        group: 'Location (job site)',
        name: 'parcel_apn',
        origin: 'Source',
        type: 'STRING',
        description: 'Assessor parcel number, when the source publishes one. Use to join to the parcels dataset.',
        example: '5148-002-013',
    },
    {
        group: 'Location (job site)',
        name: 'latitude',
        origin: 'Source',
        type: 'FLOAT64',
        description: 'WGS84 latitude. Source-published or geocoded.',
        example: '34.0537',
    },
    {
        group: 'Location (job site)',
        name: 'longitude',
        origin: 'Source',
        type: 'FLOAT64',
        description: 'WGS84 longitude. Source-published or geocoded.',
        example: '-118.2428',
    },
    {
        group: 'Location (job site)',
        name: 'address_geocodeable',
        origin: 'Derived',
        type: 'BOOL',
        description: "Whether the ADDRESS STRING looks complete enough for US to geocode it (has at least a street number + street name, no PO box) — a per-source address-quality gate. This is NOT whether the permit is located: a permit can carry published coordinates (geo_source='source') while address_geocodeable=false (hundreds of thousands of coordinate-carrying permits have address_geocodeable=false). For 'is this permit located' use `is_located` / `latitude IS NOT NULL`.",
        example: 'true',
    },
    {
        group: 'Location (job site)',
        name: 'address_key',
        origin: 'Derived',
        type: 'STRING',
        description: 'A normalized address key (street number, street and ZIP) for grouping permits at the same address.',
        example: '123|main st|4',
    },
    {
        group: 'Location (job site)',
        name: 'row_locality_place_geoid',
        origin: 'Derived',
        type: 'STRING',
        description: 'Census place GEOID of the locality the job site falls in, which can differ from the issuing jurisdiction (for example, a county permit inside a city).',
        example: '0644000',
    },
    {
        group: 'Location (job site)',
        name: 'std_address_line1',
        origin: 'Derived',
        type: 'STRING',
        description: "Standardized street address (house number + street name), cleaned during geocoding: the matched parcel's address for a parcel match, else house number + the normalized street key for a TIGER/street/source match. NULL when no clean street could be derived. Prefer over the raw address_line1 (which often carries the full 'STREET, CITY, ST ZIP' string) for street-level matching.",
        example: '123 MAIN ST',
    },
    {
        group: 'Location (job site)',
        name: 'std_city',
        origin: 'Derived',
        type: 'STRING',
        description: 'Standardized city — the RELIABLE jurisdiction place name resolved by the pipeline (same value as jurisdiction_name), NOT the noisy city_from_mapper. Use for city-scoped matching/aggregation. NULL when no jurisdiction place resolved.',
        example: 'Los Angeles',
    },
    {
        group: 'Location (job site)',
        name: 'std_state',
        origin: 'Derived',
        type: 'STRING',
        description: 'Standardized 2-letter state — jurisdiction_state, the one reliably-clean location field. Always populated for credited permits.',
        example: 'CA',
    },
    {
        group: 'Location (job site)',
        name: 'std_postal_code',
        origin: 'Derived',
        type: 'STRING',
        description: "Standardized 5-digit ZIP: the normalized permit ZIP (float/9-digit/all-zero junk cleaned,, else the matched parcel's ZIP, else the Census TIGER interpolation segment's ZIP. NULL when none available. Use instead of the raw postal_code (~40% empty).",
        example: '90012',
    },
    {
        group: 'Location (job site)',
        name: 'parcel_apn_source',
        origin: 'Derived',
        type: 'STRING',
        description: "How parcel_apn was obtained: 'source' (APN published on the permit) > 'parcel_address' (backfilled from the parcel matched by address —, so address-matched permits also gain a parcel APN). NULL = no APN.",
        example: 'source',
    },
    {
        group: 'Location (job site)',
        name: 'matched_parcel_id',
        origin: 'Derived',
        type: 'STRING',
        description: "parcel_id of the parcel that supplied this permit's geocode (via APN match in tier 2 or address match in tier 3), for permit->parcel linkage into parcels_latest. NULL when the geocode came from source coords or TIGER.",
        example: '3b1f6c0d9a2e4f7b8c5d1e0a9f3b7c2d4e6f8a1b3c5d7e9f0a2b4c6d8e0f1a3b',
    },
    {
        group: 'Location (job site)',
        name: 'geo_source',
        origin: 'Derived',
        type: 'STRING',
        description: "The TIER that produced this permit's latitude/longitude, in decreasing precision: 'source' (coords published on the permit) > 'parcel_apn' (our parcel matched by APN, parcel centroid) > 'parcel_address' (parcel matched by address) > 'tiger_addrfeat' (Census TIGER house-number interpolation) > 'tiger_street' (street-centroid fallback) > 'census' (Census Geocoder) > 'zip_centroid' (the centre of the permit's ZIP code, the least precise tier, used only when nothing better matched). NULL = NOT located. `is_located` is the boolean shortcut (geo_source IS NOT NULL).",
        example: 'parcel_apn',
    },
    {
        group: 'Location (job site)',
        name: 'is_located',
        origin: 'Derived',
        type: 'BOOL',
        description: "TRUE iff this permit has a resolved coordinate — the served latitude (any tier: source coords, parcel centroid, TIGER interpolation, Census, or ZIP centre) is non-null. USE THIS for 'is this permit located', NOT address_geocodeable (which only says whether the ADDRESS STRING looks geocodeable-by-us — a permit can carry published coordinates while address_geocodeable=false). Equivalent to `latitude IS NOT NULL`. See geo_source for the tier that produced the coordinate.",
        example: 'true',
    },
    {
        group: 'Location (job site)',
        name: 'address_source',
        origin: 'Derived',
        type: 'STRING',
        description: "How the address (address_line1/city/postal_code) was obtained: 'source' (published on the permit) > 'parcel_apn' (backfilled from matched parcel data via APN) > 'parcel_address' (from the parcel matched by address) > 'parcel_point' (the address of the parcel containing the permit's published coordinates, used when the permit came with coordinates but no usable address; geo_source stays 'source') > 'tiger_reverse' (interpolated from the nearest Census TIGER street segment to the permit's located point: its own coordinates, else its matched parcel's centre; geo_source keeps naming that locator). 'parcel_apn_situs' marks a small number of hand-corrected rows (the situs address of the parcel matched by the permit's APN). NULL = no address.",
        example: 'source',
    },
    {
        group: 'Location (job site)',
        name: 'city_from_mapper',
        origin: 'Derived',
        type: 'STRING',
        description: 'City as parsed/standardized by the mapper from the source address, used to resolve the correct jurisdiction_place_geoid for single-county and multi-city feeds. May differ from the raw published city.',
        example: 'Los Angeles',
    },
    {
        group: 'Parties',
        name: 'applicant_name',
        origin: 'Source',
        type: 'STRING',
        description: 'Name of the permit applicant, when published.',
        example: 'Jane Builder',
    },
    {
        group: 'Parties',
        name: 'contractor_name',
        origin: 'Source',
        type: 'STRING',
        description: 'Name of the contractor responsible for the work.',
        example: 'ACME Construction',
    },
    {
        group: 'Parties',
        name: 'contractor_license',
        origin: 'Source',
        type: 'STRING',
        description: 'State contractor license number, when published.',
        example: 'C10-982341',
    },
    {
        group: 'Parties',
        name: 'owner_name',
        origin: 'Source',
        type: 'STRING',
        description: 'Property owner name on the permit. Note: parcel ownership is in the parcels dataset; this field is permit-side.',
        example: 'John Owner',
    },
    {
        group: 'Structural metrics',
        name: 'square_footage',
        origin: 'Source',
        type: 'INT64',
        description: 'Square footage of the work scope, when reported.',
        example: '2400',
    },
    {
        group: 'Structural metrics',
        name: 'units_added',
        origin: 'Source',
        type: 'INT64',
        description: 'Residential units added by this permit.',
        example: '1',
    },
    {
        group: 'Structural metrics',
        name: 'units_removed',
        origin: 'Source',
        type: 'INT64',
        description: 'Residential units removed by this permit.',
        example: '0',
    },
    {
        group: 'Structural metrics',
        name: 'stories',
        origin: 'Source',
        type: 'INT64',
        description: 'Number of stories in the structure.',
        example: '2',
    },
    {
        group: 'Structural metrics',
        name: 'zoning',
        origin: 'Source',
        type: 'STRING',
        description: 'Zoning designation, as published on the permit.',
        example: 'R1',
    },
    {
        group: 'Jurisdiction',
        name: 'jurisdiction_state',
        origin: 'Derived',
        type: 'STRING',
        description: 'Two-letter state code of the jurisdiction. Always set; used as a clustering key in BigQuery.',
        example: 'CA',
    },
    {
        group: 'Jurisdiction',
        name: 'jurisdiction_name',
        origin: 'Derived',
        type: 'STRING',
        description: "Human-readable name of the issuing jurisdiction, populated for EVERY row: the manifest place_name when known, otherwise derived from source_id (e.g. 'permits_ca_santa_ana_city' -> 'Santa Ana'). The source-derived fallback is the jurisdiction's own identity, so a city permit is never labelled with its county name.",
        example: 'Los Angeles',
    },
    {
        group: 'Jurisdiction',
        name: 'jurisdiction_county_fips',
        origin: 'Derived',
        type: 'STRING',
        description: '5-digit county FIPS code of the jurisdiction (Census standard).',
        example: '06037',
    },
    {
        group: 'Jurisdiction',
        name: 'jurisdiction_place_geoid',
        origin: 'Derived',
        type: 'STRING',
        description: 'Census Place GEOID for city/town jurisdictions, when applicable.',
        example: '0644000',
    },
    {
        group: 'Display narrative',
        name: 'source_descriptive_text',
        origin: 'Source',
        type: 'STRING',
        description: "RARELY-SET raw passthrough (~1.6% of permits): labeled newline-separated text from publisher descriptive fields (notes, comments, work_description, project_description, remarks, …) that have no canonical column. This is NOT the primary descriptive field — it is folded into `description_full` (~95.7% populated), which is what to read for a permit's description. Kept for provenance/completeness.",
        example: 'Scope: new SFR + garage',
    },
    {
        group: 'Display narrative',
        name: 'description_full',
        origin: 'Derived',
        type: 'STRING',
        description: "PRIMARY human-readable descriptive text — the 'what is this permit about' field, populated for ~95.7% of permits. Auto-composed by the mapper as a labeled newline-separated narrative from permit_type, permit_subtype, permit_class, work_type, description, status, and source_descriptive_text. Prefer this over the individual descriptive columns for display; it is by far the most complete descriptive field (source_descriptive_text alone is only ~1.6%). Read-only — never set directly by mappers.",
        example: 'Type: Building\nDescription: New single-family dwelling',
    },
    {
        group: 'Display narrative',
        name: 'primary_descriptive_text',
        origin: 'Derived',
        type: 'STRING',
        description: "SINGLE headline 'what is this permit about' field: COALESCE of description_full > description > source_descriptive_text > permit_subtype > permit_type — the best available free-text description in one column. Near-universal coverage (~98.8%) because descriptive text is spread across these fields rather than concentrated in the rarely-set source_descriptive_text (~1.6%). Prefer description_full (~95.7%) for the full labeled narrative; use this when you want one always-there descriptive column.",
        example: 'Type: Building\nDescription: New single-family dwelling',
    },
];
//# sourceMappingURL=fieldData.js.map